/**
 * The «Провалил ЕВИ» fact fold — T.Z113.
 *
 * TWO stored booleans say the same thing about a child, from two different writers:
 *
 *   * `applications.evi_failed` (migration 0031) — the AUTO fact: the worker's
 *     registry engine marks a child who was in the ЕВИ pass data and is ABSENT from
 *     the freshest ЕВИ file with no FTP evidence (`evi_passed_at IS NULL`), and
 *     clears it when the child reappears in a later file. Reversible by the sync.
 *   * `applications.evi_failed_manually` (migration 0026, T.Z96) — the MANUAL fact:
 *     a MANAGER/ADMIN states it by hand for children whose ЕВИ history cannot
 *     derive the fact. Never touched by any source (the T.Z96 invariant).
 *
 * Every reader of the fact — the register's chip, the profile chip, the export
 * cell, the «Провалил ЕВИ» filter predicate — folds them HERE, once, so the two
 * sources can never render as two chips or disagree between surfaces. NOT a funnel
 * status: neither column is in `deriveStatus`/`guardRollingQueue`. T.Z143 (2026-09-18,
 * owner: «Провалил ЕВИ must BE a status, not a flag») — the fact now SURFACES as the
 * row's display status and as a `?status=EVI_FAILED` filter value (see
 * {@link displayStatusOf} / {@link EVI_FAILED_STATUS_TOKEN}); the six-value `AppStatus`
 * stays frozen and no stored column ever holds the token.
 */
import { EVI_NOT_PASSED_STATUS_TOKEN, isEviNotPassed } from './evi-not-passed';
/** `application_events.field` the worker journals the auto fact on (the column's own name). */
export const EVI_FAILED_FIELD = 'eviFailed';
/** The chip label when the AUTO fact is the (only) source. */
export const EVI_FAILED_CHIP_LABEL = 'Провалил ЕВИ';
/** The chip label when the MANUAL fact is the only source (T.Z96's own wording, kept). */
export const EVI_FAILED_MANUAL_CHIP_LABEL = 'Провалил ЕВИ (вручную)';
/** The drawer's journal label for the AUTO fact — who set it and why, in one phrase. */
export const EVI_FAILED_JOURNAL_LABEL = 'Провалил ЕВИ (авто, исчез из файла ЕВИ)';
/**
 * The ONE fold: `evi_failed OR evi_failed_manually` is «the child failed the ЕВИ»,
 * and the source tells the chip which caption to print (one chip, never two).
 * `undefined`/`null` flags read as false — a hand-built row double, never a Prisma
 * answer, is as good as «not set».
 */
export function eviFailedSourceOf(row) {
    const auto = row.eviFailed === true;
    const manual = row.eviFailedManually === true;
    if (auto && manual)
        return 'both';
    if (auto)
        return 'auto';
    if (manual)
        return 'manual';
    return null;
}
/** The boolean every filter/export cell speaks: does this row carry the fact at all? */
export function isEviFailed(row) {
    return eviFailedSourceOf(row) !== null;
}
/**
 * T.Z143 — the VIRTUAL status token. The owner's ask: «Провалил ЕВИ» must BE a status —
 * in the register's «Статус» selectbox, in the `?status=` filter grammar and in the
 * badge where the status is displayed. It is NOT a sixth+1 funnel status: the six-value
 * `AppStatus` is frozen (05 §4.1), `deriveStatus` never reads the fact columns and no
 * stored `applications.status` ever carries this token. It exists on the WIRE (a
 * `?status=` value, #3 and #18 alike) and in the UI's display layer, and it resolves to
 * the SAME fold the chip speaks ({@link isEviFailed} — either source), so a row that
 * displays «Провалил ЕВИ» is exactly a row the `?status=EVI_FAILED` filter answers.
 */
export const EVI_FAILED_STATUS_TOKEN = 'EVI_FAILED';
/**
 * The row's status AS THE OWNER SEES IT (T.Z143): «Провалил ЕВИ» when either fact
 * source holds, the stored status otherwise. The dashboard funnel and every count keep
 * reading the STORED status (the fact is an axis on top of it — a failed child still
 * occupies his base-status bucket there); this fold is the register/profile badge and
 * the `?status=` virtual predicates, nothing else.
 *
 * T.Z204 — the fold's PRECEDENCE, in evaluation order:
 *
 *   1. «Провалил ЕВИ» (either fact source) — the stronger claim: a vanished child is
 *      a fact about the child, a lapsed deadline is a fact about the clock. A flagged
 *      row NEVER reads «Не прошёл ЕВИ», however old its link.
 *   2. «Не прошёл ЕВИ» — LINK_RECEIVED past its ЕВИ deadline with no pass, judged ONLY
 *      when the caller passes `now` (see {@link DisplayStatusInput}). The predicate is
 *      the shared `isEviNotPassed`, the same one the `?status=EVI_NOT_PASSED` SQL arm
 *      and the file's «Статус» cell speak.
 *   3. The stored status.
 */
export function displayStatusOf(row, now) {
    if (eviFailedSourceOf(row) !== null)
        return EVI_FAILED_STATUS_TOKEN;
    if (now !== undefined && isEviNotPassed(row, now))
        return EVI_NOT_PASSED_STATUS_TOKEN;
    return row.status;
}
/**
 * The chip of one row: `null` = render nothing; otherwise the single label the chip
 * prints (the AUTO wording when the sync said it, the T.Z96 manual wording when only
 * the human did — one chip either way, per the owner's reading of the card).
 */
export function eviFailedChip(row) {
    const source = eviFailedSourceOf(row);
    if (source === null)
        return null;
    return {
        on: true,
        label: source === 'manual' ? EVI_FAILED_MANUAL_CHIP_LABEL : EVI_FAILED_CHIP_LABEL,
    };
}
//# sourceMappingURL=evi-failed.js.map